import _ from "lodash";

import { addExternalResource, getExternalReferenceForReference } from "../actions/activityActions";
import { getResourceReference, isDriveReference } from "../actions/drive";
import { copy, getClipboard, isClipboardEmpty } from "./clipboard";

export const MEDIA_RESOURCE_CLIPBOARD_TYPE = "ambifi/media-resource";

function cloneIfPresent(value) {
	return value === null || typeof value === "undefined" ? null : _.cloneDeep(value);
}

function compactDriveResource(resource) {
	if (!_.isPlainObject(resource)) {
		return null;
	}

	const result = {};
	[
		"guid",
		"revision",
		"type",
		"path",
		"name",
		"private",
		"scope",
		"contentType",
		"fullPath",
		"metaData"
	].forEach((key) => {
		if (!_.isNil(resource[key])) {
			result[key] = cloneIfPresent(resource[key]);
		}
	});

	return result.guid ? result : null;
}

function getExternalResourceForReference(reference) {
	if (!isDriveReference(reference)) {
		return null;
	}

	try {
		return getExternalReferenceForReference(reference);
	} catch (err) {
		console.warn("Unable to resolve external resource for copied media reference", err);
		return null;
	}
}

function parseMediaResourceClipboardPayload(value) {
	if (_.isPlainObject(value) && value.type === MEDIA_RESOURCE_CLIPBOARD_TYPE) {
		return value;
	}

	if (!_.isString(value)) {
		return null;
	}

	const trimmed = value.trim();
	if (!trimmed.startsWith("{")) {
		return null;
	}

	try {
		const parsed = JSON.parse(trimmed);
		return _.isPlainObject(parsed) && parsed.type === MEDIA_RESOURCE_CLIPBOARD_TYPE ? parsed : null;
	} catch (err) {
		return null;
	}
}

function parseResourceReference(value) {
	if (isDriveReference(value)) {
		return value;
	}

	if (!_.isString(value)) {
		return null;
	}

	const trimmed = value.trim();
	if (!trimmed.startsWith("{")) {
		return null;
	}

	try {
		const parsed = JSON.parse(trimmed);
		return isDriveReference(parsed) ? parsed : null;
	} catch (err) {
		return null;
	}
}

export function buildMediaResourceClipboardValue(reference, driveResource = null) {
	const externalResource = getExternalResourceForReference(reference);
	return {
		type: MEDIA_RESOURCE_CLIPBOARD_TYPE,
		version: 1,
		reference: isDriveReference(reference) ? cloneIfPresent(reference) : null,
		resource: compactDriveResource(driveResource) || compactDriveResource(externalResource),
		externalResource: compactDriveResource(externalResource),
		copiedAt: Date.now()
	};
}

export function copyMediaResourceToClipboard(reference, driveResource = null) {
	return copy(buildMediaResourceClipboardValue(reference, driveResource));
}

function getReferenceFromMediaResourcePayload(payload) {
	const resource = compactDriveResource(payload.resource) || compactDriveResource(payload.externalResource);
	if (resource) {
		const externalResourceMetaData = _.get(payload, ["externalResource", "metaData"], null);
		const referenceMetaData = _.get(payload, ["reference", "metaData"], null);
		const refId = addExternalResource(resource, externalResourceMetaData);
		return getResourceReference(refId, referenceMetaData);
	}

	return parseResourceReference(payload.reference);
}

export function getClipboardValueForMediaResource() {
	const value = getClipboard();
	if (_.isNil(value) || value === "") {
		return null;
	}

	if (_.isString(value) && value.trim() === "[object Object]") {
		return null;
	}

	const payload = parseMediaResourceClipboardPayload(value);
	if (payload) {
		return getReferenceFromMediaResourcePayload(payload);
	}

	const parsedReference = parseResourceReference(value);
	if (parsedReference) {
		return parsedReference;
	}

	return value;
}

export function isMediaResourceClipboardEmpty() {
	return isClipboardEmpty();
}
